import {zodResolver} from '@hookform/resolvers/zod';
import {Button, Card, Grid, MenuItem, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import {RhfTextField} from 'mui-rhf-integration';
import type * as React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {z} from 'zod';
import {emailValidator} from '@/regex/emailValidator.ts';
import StatementSearchResultsTable from '@/components/StatementSearchResultsTable/StatementSearchResultsTable';
import {errorMap} from '@/utils/zod.ts';
import YearSelectorField from "@/components/YearSelectorField";

const schema = z.object({
    bu: z.string().optional().default(''),
    bfsAcct: z.string().optional().default(''),
    fund: z.string().optional().default(''),
    deptId: z.string().optional().default(''),
    program: z.string().optional().default(''),
    cf1: z.string().optional().default(''),
    cf2: z.string().optional().default(''),
    month: z.string().optional().default(''),
    year: z.string().optional().default(''),
    amount: z.string().optional().default(''),
    statementRecipient: z.string().optional().default(''),
    email: z.string().refine(emailValidator, {message: 'Email format is xxxx@xxxx.xx.'}).default('').optional(),
});

export type StatementSearchSchema = z.infer<typeof schema>;

const StatementSearchForm = () : React.ReactElement => {
    const form = useForm<StatementSearchSchema>({
        resolver: zodResolver(schema, {errorMap}),
        defaultValues: {
            bu: '',
            bfsAcct: '',
            fund: '',
            deptId: '',
            program: '',
            cf1: '',
            cf2: '',
            month: '',
            year: '',
            amount: '',
            statementRecipient: '',
            email: undefined,
        },
    });

    return <FormProvider {...form}>
        <form onSubmit={
            form.handleSubmit(async (_data, event) => {
                event?.stopPropagation();
            })
        }>
            <Card sx={{p: 2}}>

                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Typography variant='h6'>Chart of Accounts</Typography>
                    </Grid>
                    <Grid size={{xs: 6, md: 1}}>
                        <RhfTextField
                            control={form.control}
                            name="bu"
                            label="BU"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="bfsAcct"
                            label="BFS Acct"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="fund"
                            label="Fund"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="deptId"
                            label="Dept ID"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 1}}>
                        <RhfTextField
                            control={form.control}
                            name="program"
                            label="Program"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="cf1"
                            label="CF1"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="cf2"
                            label="CF2"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={12} sx={{mt: 2}}>
                        <Typography variant='h6'>Date Posted, Amount Posted, and Recipients</Typography>
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            select
                            control={form.control}
                            name="month"
                            label="Month"
                            variant="outlined"
                            sx={{width: '100%'}}
                        >
                            <MenuItem value={'1'}>1</MenuItem>
                            <MenuItem value={'2'}>2</MenuItem>
                            <MenuItem value={'3'}>3</MenuItem>
                            <MenuItem value={'4'}>4</MenuItem>
                            <MenuItem value={'5'}>5</MenuItem>
                            <MenuItem value={'6'}>6</MenuItem>
                            <MenuItem value={'7'}>7</MenuItem>
                            <MenuItem value={'8'}>8</MenuItem>
                            <MenuItem value={'9'}>9</MenuItem>
                            <MenuItem value={'10'}>10</MenuItem>
                            <MenuItem value={'11'}>11</MenuItem>
                            <MenuItem value={'12'}>12</MenuItem>
                        </RhfTextField>
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <YearSelectorField
                            name='year'
                            label='Year'
                            control={form.control}
                            startYear={(new Date().getFullYear() - 6)}
                            endYear={(new Date().getFullYear())}
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 2}}>
                        <RhfTextField
                            control={form.control}
                            name="amount"
                            label="Amount"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 6, md: 3}}>
                        <RhfTextField
                            control={form.control}
                            name="statementRecipient"
                            label="Statement Recipient"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 3}}>
                        <RhfTextField
                            control={form.control}
                            name="email"
                            label="Email"
                            variant="outlined"
                            sx={{width: '100%'}}
                        />
                    </Grid>
                    <Grid size={12} sx={{display: 'flex', justifyContent: 'center', mt: 2, gap: 1}}>
                        <Stack sx={{flexDirection: 'row', gap: 1}}>
                            <Button
                                variant='outlined'
                                type='submit'
                            >
                                Start Search
                            </Button>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    form.reset();
                                }}
                            >
                                Clear Search
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Card>
        </form>
        <StatementSearchResultsTable/>
    </FormProvider>;
};

export default StatementSearchForm;
